import { useEffect, useState } from 'react';

import { useSwipeable } from 'react-swipeable';
import { Attachments, CloudFile } from './FileViewer';
import { Button } from 'src/ui/button';
import { Typography } from 'src/ui/typography';
import { useMediaQuery } from 'src/ui/hooks/useMediaQuery';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'src/utils/@/lib/utils';


interface ThumbnailsProps {
  attachments: Attachments | [];
  selected: any;
  onThumbnailSelect: (index: any) => void;
}

export const NEXT = 'NEXT';
export const PREV = 'PREV';
type Direction = typeof PREV | typeof NEXT;

export const Thumbnails: React.FC<ThumbnailsProps> = ({
  attachments,
  selected,
  onThumbnailSelect,
}) => {
  const [firstShownIndex, setFirstShownIndex] = useState(0 as number);
  const smBelow = useMediaQuery('(max-width: 639px)');

  useEffect(() => {
    if (
      attachments
        .map(function (x: any) {
          return x.id;
        })
        .indexOf(selected.id) >=
      firstShownIndex + (smBelow ? 2 : 3)
    ) {
      setFirstShownIndex(
        () =>
          attachments
            .map(function (x: any) {
              return x.id;
            })
            .indexOf(selected.id) - (smBelow ? 1 : 2)
      );
    }

    if (
      firstShownIndex >= 1 &&
      attachments
        .map(function (x: any) {
          return x.id;
        })
        .indexOf(selected.id) <=
        firstShownIndex - 1
    ) {
      setFirstShownIndex((prevIndex) => prevIndex - 1);
    }
    // eslint-disable-next-line
  }, [selected, attachments, smBelow, setFirstShownIndex]);

  const slide = (dir: Direction) => {
    if (dir === NEXT && attachments[firstShownIndex + (smBelow ? 2 : 3)]) {
      setFirstShownIndex(firstShownIndex + 1);
      return;
    }
    if (dir === PREV && attachments[firstShownIndex - 1]) {
      setFirstShownIndex(firstShownIndex - 1);
      return;
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => slide(NEXT),
    onSwipedRight: () => slide(PREV),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  return attachments.length <= 0 ? (
    <></>
  ) : (
    <div
      {...handlers}
      className="w-full px-[65px] flex flex-wrap max-w-full justify-center items-center min-h-[150px] relative bg-card rounded-lg shadow-md"
    >
      <div className="absolute left-0 pl-[25px]">
        <Button
          variant="ghost"
          size="icon"
          disabled={
            attachments[firstShownIndex - (smBelow ? 1 : 1)] ? false : true
          }
          onClick={() => slide(PREV)}
        >
          <ChevronLeft />
        </Button>
      </div>
      {attachments.map((attachment, i) => {
        const payload = attachment?.payload as { attachment: CloudFile };

        return (
          <div
            key={i}
            onClick={() => onThumbnailSelect(attachment)}
            className={cn(
              'm-[3px] h-full justify-center items-center',
              selected.id === attachment.id ? 'border-2 border-primary' : 'border-0',
              i > firstShownIndex + (smBelow ? 1 : 2) ||
                i < firstShownIndex + (smBelow ? 0 : 0)
                ? 'hidden'
                : 'flex'
            )}
          >
            {payload.attachment.ext !== 'jpeg' &&
            payload.attachment.ext !== 'png' &&
            payload.attachment.ext !== 'jpg' &&
            payload.attachment.ext !== 'gif' ? (
              <div
                key={i}
                className="w-[100px] min-h-[100px] max-h-[100px] flex justify-center flex-col items-center"
              >
                <span
                  className={`fiv-viv fiv-icon-${payload.attachment.ext} fiv-size-lg`}
                  style={{ fontSize: '80px' }}
                ></span>
                <Typography
                  variant="caption"
                  className="text-center mt-2 overflow-hidden text-ellipsis w-[100px]"
                >
                  {payload.attachment.originalname}
                </Typography>
              </div>
            ) : (
              <div className="w-[100px] min-h-[100px] max-h-[100px] flex justify-center flex-col items-center">
                <img
                  key={i}
                  className="align-middle h-[100px] leading-none max-w-[100px] min-w-[100px]"
                  src={payload.attachment.file}
                  alt={payload.attachment.originalname}
                />
                <Typography
                  variant="caption"
                  className="text-center truncate mt-2 overflow-hidden text-ellipsis w-[100px]"
                >
                  {payload.attachment.originalname}
                </Typography>
              </div>
            )}
          </div>
        );
      })}
      <div className="absolute right-0 pr-[25px]">
        <Button
          variant="ghost"
          size="icon"
          disabled={
            attachments[firstShownIndex + (smBelow ? 2 : 3)] ? false : true
          }
          onClick={() => slide(NEXT)}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
};
